import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { CloseIcon } from './MangaIcons';

export const TRANSLATION_OPTIONS = [
    { value: 'all', label: 'Все' },
    { value: 'ongoing', label: 'Выходит' },
    { value: 'completed', label: 'Завершён' },
];

export const ORDER_OPTIONS = [
    { value: '', label: 'По умолчанию' },
    { value: 'new', label: 'Новые' },
    { value: 'rating', label: 'По оценке' },
    { value: 'title', label: 'По алфавиту' },
];

export const EMPTY_FILTERS = { genres: [], translation: 'all', order: '' };

export const countActiveFilters = (filters) => filters.genres.length
    + (filters.translation !== 'all' ? 1 : 0)
    + (filters.order ? 1 : 0);

/**
 * Filter sheet of the manga catalog, the same layout as in the app: status, sorting and genres as pills,
 * changes are applied with one button. A centred dialog on desktop and a bottom sheet on phones.
 */
const MangaCatalogFilters = ({ open, value, genres, onApply, onClose }) => {
    const [draft, setDraft] = useState(value);
    const closeRef = useRef(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    // Start from the applied filters each time the sheet opens; edits stay a draft until "Применить".
    useEffect(() => {
        if (open) {
            setDraft(value);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const handleKey = (event) => {
            if (event.key === 'Escape') {
                onCloseRef.current();
            }
        };
        document.addEventListener('keydown', handleKey);
        closeRef.current?.focus();
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleKey);
        };
    }, [open]);

    if (!open) {
        return null;
    }

    const toggleGenre = (name) => setDraft((current) => ({
        ...current,
        genres: current.genres.includes(name)
            ? current.genres.filter((genre) => genre !== name)
            : [...current.genres, name],
    }));

    const pill = (selected, label, onClick, count) => (
        <button
            key={label}
            type="button"
            className={selected ? 'tab-button selected' : 'tab-button'}
            aria-pressed={selected}
            onClick={onClick}
        >
            {label}
            {count ? <span className="manga-filter-count">{count}</span> : null}
        </button>
    );

    return createPortal(
        <div className="manga-filter-overlay" onClick={onClose}>
            <div
                className="manga-filter-sheet"
                role="dialog"
                aria-modal="true"
                aria-labelledby="manga-filter-title"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="manga-filter-sheet__handle" aria-hidden="true" />
                <div className="manga-filter-sheet__head">
                    <h2 id="manga-filter-title">Фильтры</h2>
                    <button
                        type="button"
                        className="manga-filter-reset"
                        disabled={countActiveFilters(draft) === 0}
                        onClick={() => setDraft(EMPTY_FILTERS)}
                    >
                        Сбросить
                    </button>
                    <button
                        type="button"
                        ref={closeRef}
                        className="manga-icon-button"
                        aria-label="Закрыть фильтры"
                        onClick={onClose}
                    >
                        <CloseIcon size={22} />
                    </button>
                </div>

                <div className="manga-filter-sheet__body">
                    <section className="manga-filter-section">
                        <h3>Статус перевода</h3>
                        <div className="manga-pill-group">
                            {TRANSLATION_OPTIONS.map((option) => pill(
                                draft.translation === option.value,
                                option.label,
                                () => setDraft((current) => ({ ...current, translation: option.value }))
                            ))}
                        </div>
                    </section>
                    <section className="manga-filter-section">
                        <h3>Сортировка</h3>
                        <div className="manga-pill-group">
                            {ORDER_OPTIONS.map((option) => pill(
                                draft.order === option.value,
                                option.label,
                                () => setDraft((current) => ({ ...current, order: option.value }))
                            ))}
                        </div>
                    </section>
                    <section className="manga-filter-section">
                        <h3>Жанры</h3>
                        <div className="manga-pill-group">
                            {genres.map((genre) => pill(
                                draft.genres.includes(genre.name),
                                genre.name,
                                () => toggleGenre(genre.name),
                                genre.count
                            ))}
                        </div>
                    </section>
                </div>

                <div className="manga-filter-sheet__foot">
                    <button
                        type="button"
                        className="ui-btn ui-btn--light ui-btn--block manga-filter-apply"
                        onClick={() => onApply(draft)}
                    >
                        Применить
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

MangaCatalogFilters.propTypes = {
    open: PropTypes.bool.isRequired,
    value: PropTypes.shape({
        genres: PropTypes.arrayOf(PropTypes.string),
        translation: PropTypes.string,
        order: PropTypes.string,
    }).isRequired,
    genres: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, count: PropTypes.number })).isRequired,
    onApply: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default MangaCatalogFilters;
